import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bike,
  Calendar,
  Camera,
  ChevronLeft,
  ChevronRight,
  Copy,
  Fuel,
  Gauge,
  Heart,
  Home,
  Layers,
  Mail,
  MapPin,
  MessageCircle,
  Palette,
  Pencil,
  Phone,
  Settings,
  Share2,
  ShieldCheck,
  Wrench,
  Sparkles,
  Clock,
  Maximize2,
  X,
} from 'lucide-react';
import BoostPostModal from './BoostPostModal';
import { FaWhatsapp } from 'react-icons/fa6';
import { getVehicleById } from '../api/bikeApi';
import { useAuth } from './AuthContext';
import { resolveMediaUrl } from '../utils/resolveMediaUrl';
import './VehicleDetails.css';
import verifiedIcon from '../Images/verififedbutton.png';

const hasValue = value => value !== undefined && value !== null && value !== '';

const formatNumber = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return number.toLocaleString('en-LK', { maximumFractionDigits: 0 });
};

const formatPrice = price => (hasValue(price) ? `Rs: ${formatNumber(price)}` : 'Negotiable');

const formatDate = date => {
  if (!date) return 'Recently';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? 'Recently' : parsed.toISOString().slice(0, 10);
};

const onlyDigits = value => (value || '').replace(/\D/g, '');

const whatsappPhoneFromLK = phone => {
  const digits = onlyDigits(phone);
  if (!digits) return '';
  if (digits.startsWith('0')) return `94${digits.slice(1)}`;
  return digits.startsWith('94') ? digits : `94${digits}`;
};

const formatMileage = value => (hasValue(value) ? `${formatNumber(value)} km` : null);
const formatEngine = value => (hasValue(value) ? `${formatNumber(value)} cc` : null);

const getInitials = name => {
  if (!name) return 'BE';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'BE';
  return parts.slice(0, 2).map(part => part[0]).join('').toUpperCase();
};

const buildDescription = vehicle => {
  const makeLine = [vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'bike';
  const condition = vehicle.condition ? `${vehicle.condition.toLowerCase()} ` : '';
  const year = vehicle.year ? ` from ${vehicle.year}` : '';
  const location = vehicle.location ? ` in ${vehicle.location}` : '';
  const mileage = hasValue(vehicle.mileageKm)
    ? ` It has done approximately ${formatNumber(vehicle.mileageKm)} km.`
    : '';

  return `This ${condition}${makeLine}${year} is listed${location}.${mileage} Contact the seller to confirm availability, documents, inspection time, and final price.`;
};

const VehicleDetails = ({ allVehicles = [] }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedVehicle = location.state?.vehicle;
  const { user } = useAuth();

  const [idx, setIdx] = useState(0);
  const [remoteVehicle, setRemoteVehicle] = useState(null);
  const [remoteError, setRemoteError] = useState('');
  const [saved, setSaved] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);

  const handleBack = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    let lastNav = null;
    try {
      lastNav = JSON.parse(sessionStorage.getItem('bikeeka_last_listing_nav') || 'null');
    } catch (_) {}

    const scrollY = location.state?.fromListingScroll ?? lastNav?.scrollY;
    const cardId = location.state?.fromCardId ?? lastNav?.cardId;
    const fromPath = location.state?.fromPath ?? lastNav?.fromPath ?? '/';

    if (typeof scrollY === 'number' || cardId) {
      try {
        sessionStorage.setItem('bikeeka_restore_scroll', JSON.stringify({
          scrollY,
          cardId,
          fromPath,
          timestamp: Date.now()
        }));
      } catch (_) {}
    }

    if (window.history.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(fromPath);
    }
  };

  useEffect(() => {
    setIdx(0);
    setShareMessage('');
  }, [id]);

  const vehicle = useMemo(
    () => (passedVehicle && String(passedVehicle.id) === String(id) ? passedVehicle : null) || allVehicles.find(v => String(v.id) === String(id)),
    [passedVehicle, allVehicles, id]
  );

  useEffect(() => {
    let alive = true;
    setRemoteError('');

    if (id) {
      getVehicleById(id)
        .then(data => {
          if (!alive) return;
          setRemoteVehicle(data);
        })
        .catch(error => {
          if (!alive) return;
          if (!vehicle) {
            console.error(error);
            setRemoteError('Failed to load listing from server.');
            setRemoteVehicle(null);
          }
        });
    }

    return () => {
      alive = false;
    };
  }, [vehicle, id]);

  const activeVehicle = vehicle || remoteVehicle;



  const photos = useMemo(() => {
    if (!activeVehicle) return [];
    const gallery = activeVehicle.gallery?.length ? activeVehicle.gallery : [activeVehicle.image];
    return gallery.filter(Boolean).map(resolveMediaUrl);
  }, [activeVehicle]);

  const activePhoto = photos[idx] || photos[0] || '';

  const approvedRequest = useMemo(() => {
    if (!activeVehicle?.user) return null;
    return activeVehicle.user.membershipRequests?.find(r => r.status === 'approved')
      || activeVehicle.user.membershipRequests?.[0]
      || null;
  }, [activeVehicle]);

  const isShowroom = activeVehicle?.source === 'showroom' && approvedRequest;
  const sellerName = isShowroom
    ? approvedRequest.shopName
    : activeVehicle?.user?.name || activeVehicle?.sellerName || 'Private Seller';
  const sellerEmail = isShowroom
    ? approvedRequest.email
    : activeVehicle?.user?.email || activeVehicle?.email;
  const sellerPhone = isShowroom && approvedRequest?.phone ? approvedRequest.phone : activeVehicle?.phone;
  const phoneDigits = onlyDigits(sellerPhone);
  const whatsappNumber = whatsappPhoneFromLK(sellerPhone);
  const makeLine = [activeVehicle?.make, activeVehicle?.model].filter(Boolean).join(' ');
  const postedDate = formatDate(activeVehicle?.postedAt);
  const listingTitle = activeVehicle?.title || makeLine || 'Vehicle listing';
  const sellerAvatar = isShowroom && approvedRequest?.shopImage ? resolveMediaUrl(approvedRequest.shopImage) : '';
  const sellerCover = isShowroom && approvedRequest?.coverImage ? resolveMediaUrl(approvedRequest.coverImage) : '';
  const sellerTypeLabel = isShowroom ? 'Verified Showroom Dealer' : 'Registered Private Seller';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const whatsappMessage = encodeURIComponent(`Hi, I am interested in your ${listingTitle} listed on BikeEka.`);
  const emailSubject = encodeURIComponent(`Inquiry about ${listingTitle}`);
  const emailBody = encodeURIComponent(`Hi, I am interested in your ${listingTitle} listed on BikeEka. Is it still available?`);

  const heroFacts = [
    { label: 'Year', value: activeVehicle?.year || 'Not listed' },
    { label: 'Engine', value: formatEngine(activeVehicle?.engineCapacityCc) || 'Not listed' },
    { label: 'Mileage', value: formatMileage(activeVehicle?.mileageKm) || 'Not listed' },
  ];

  const specItems = [
    { icon: Bike, label: 'Make', value: activeVehicle?.make },
    { icon: Wrench, label: 'Model', value: activeVehicle?.model },
    { icon: Calendar, label: 'Year', value: activeVehicle?.year },
    { icon: Calendar, label: 'Registered', value: activeVehicle?.registerYear },
    { icon: Gauge, label: 'Mileage', value: formatMileage(activeVehicle?.mileageKm) },
    { icon: Settings, label: 'Engine', value: formatEngine(activeVehicle?.engineCapacityCc) },
    { icon: Settings, label: 'Transmission', value: activeVehicle?.transmission },
    { icon: Fuel, label: 'Fuel Type', value: activeVehicle?.fuelType },
    { icon: Layers, label: 'Condition', value: activeVehicle?.condition },
    { icon: Palette, label: 'Color', value: activeVehicle?.color },
  ];

  const highlights = [
    activeVehicle?.condition && { label: activeVehicle.condition },
    activeVehicle?.type && { label: activeVehicle.type },
    isShowroom && { label: 'Showroom', icon: Home },
  ].filter(Boolean);

  const next = () => setIdx(current => (photos.length ? (current + 1) % photos.length : 0));
  const prev = () => setIdx(current => (photos.length ? (current - 1 + photos.length) % photos.length : 0));

  const handleShare = async () => {
    setShareMessage('');
    const data = {
      title: listingTitle,
      text: `Check this listing on BikeEka: ${listingTitle}`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(data);
        setShareMessage('Shared');
        return;
      }

      if (navigator.clipboard && shareUrl) {
        await navigator.clipboard.writeText(shareUrl);
        setShareMessage('Link copied');
        return;
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error(error);
    }

    setShareMessage('Copy link from the address bar');
  };

  if (!activeVehicle && remoteError) {
    return (
      <main className="vehicle-details-page">
        <div className="vd-container">
          <div className="vd-state-card">
            <h1>Listing not found</h1>
            <p>{remoteError}</p>
            <button type="button" className="vd-back-link" onClick={handleBack}>
              <ArrowLeft size={18} aria-hidden="true" />
              Go back
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (!activeVehicle) {
    return (
      <main className="vehicle-details-page">
        <div className="vd-container">
          <div className="vd-topbar">
            <button type="button" className="vd-back-link" onClick={handleBack}>
              <ArrowLeft size={18} aria-hidden="true" />
              <span className="vd-back-link-text">Back to listings</span>
            </button>
          </div>

          <div className="vd-loading-card">
            <div className="vd-loading-graphic">
              <div className="vd-spinner-ring" />
              <Bike className="vd-loading-bike" />
            </div>
            <h2 className="vd-loading-title">Loading Listing Details...</h2>
            <p className="vd-loading-subtitle">Retrieving verified bike specs, pricing, and seller details</p>
            
            <div className="vd-loading-shimmer-box">
              <div className="vd-shimmer-line vd-shimmer-line--lg" />
              <div className="vd-shimmer-line vd-shimmer-line--md" />
              <div className="vd-shimmer-line vd-shimmer-line--sm" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="vehicle-details-page">
      <div className="vd-container">
        <div className="vd-topbar">
          <button type="button" className="vd-back-link" onClick={handleBack}>
            <ArrowLeft size={18} aria-hidden="true" />
            <span className="vd-back-link-text">Back to listings</span>
          </button>
          {user && activeVehicle?.userId && String(user.id) === String(activeVehicle.userId) && (
            <div className="vd-owner-actions">
              {activeVehicle.boostRequests?.some(r => r.status === 'pending') ? (
                <button
                  type="button"
                  className="vd-owner-btn vd-owner-btn--pending"
                  onClick={() => setIsBoostModalOpen(true)}
                >
                  <Clock size={15} className="animate-pulse" />
                  <span>Boost Pending</span>
                </button>
              ) : (!activeVehicle.approvalStatus || activeVehicle.approvalStatus === 'approved') ? (
                <button
                  type="button"
                  className="vd-owner-btn vd-owner-btn--boost"
                  onClick={() => setIsBoostModalOpen(true)}
                >
                  <Sparkles size={15} />
                  <span>Boost Listing</span>
                </button>
              ) : null}
              <Link
                to={`/edit-vehicle/${id}`}
                className="vd-owner-btn vd-owner-btn--edit"
              >
                <Pencil size={15} />
                <span>Edit Listing</span>
              </Link>
            </div>
          )}
        </div>

        <div className="vd-layout">
          <section className="vd-main-column" aria-label="Vehicle photos and details">
            <Gallery
              activePhoto={activePhoto}
              idx={idx}
              listingTitle={listingTitle}
              next={next}
              photos={photos}
              prev={prev}
              setIdx={setIdx}
            />

            <section className="vd-section">
              <div className="vd-section-header">
                <span className="vd-section-icon">
                  <Gauge size={20} aria-hidden="true" />
                </span>
                <div>
                  <p className="vd-section-kicker">Details</p>
                  <h2>Vehicle Specifications</h2>
                </div>
              </div>

              <div className="vd-spec-grid">
                {specItems.map(item => (
                  <SpecItem key={item.label} {...item} />
                ))}
                {specItems.length % 2 !== 0 && (
                  <div className="vd-spec-item vd-spec-item--empty" aria-hidden="true" />
                )}
              </div>
            </section>

            <section className="vd-section">
              <div className="vd-section-header">
                <span className="vd-section-icon">
                  <MessageCircle size={20} aria-hidden="true" />
                </span>
                <div>
                  <p className="vd-section-kicker">Seller notes</p>
                  <h2>Description</h2>
                </div>
              </div>

              <p className="vd-description">
                {activeVehicle.description || buildDescription(activeVehicle)}
              </p>
            </section>
          </section>

          <aside className="vd-sidebar" aria-label="Listing summary and seller contact">
            <section className="vd-summary-card">
              {highlights.length > 0 && (
                <div className="vd-chip-row" aria-label="Listing highlights">
                  {highlights.map(({ label, icon: Icon }) => (
                    <span className="vd-chip" key={label}>
                      {Icon && <Icon size={14} aria-hidden="true" />}
                      {label}
                    </span>
                  ))}
                </div>
              )}

              <h1 className="vd-title">{listingTitle}</h1>

              <div className="vd-meta-list">
                <MetaLine icon={MapPin} value={activeVehicle.location || 'Sri Lanka'} />
                {isShowroom && (
                  <MetaLine
                    icon={Home}
                    value={approvedRequest.shopName}
                    verified
                  />
                )}
                <MetaLine icon={Calendar} value={`Posted ${postedDate}`} />
              </div>

              <div className="vd-price-box">
                <span className="vd-price">{formatPrice(activeVehicle.price)}</span>
                {activeVehicle.negotiable && (
                  <span className="vd-price-badge">Negotiable</span>
                )}
              </div>

              {/* Boost Post Banner for Owner */}
              {user && activeVehicle?.userId && String(user.id) === String(activeVehicle.userId) && (
                activeVehicle.boostRequests?.some(r => r.status === 'pending') ? (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.15) 100%)',
                    border: '1.5px solid rgba(245, 158, 11, 0.4)',
                    borderRadius: '16px',
                    padding: '16px',
                    margin: '16px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: '#FEF3C7',
                        color: '#D97706',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '900',
                        fontSize: '16px',
                        flexShrink: 0
                      }}>
                        ⏳
                      </div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '900', color: '#0F172A' }}>
                          Boost Pending Review
                        </h4>
                        <p style={{ margin: '2px 0 0', fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                          Bank slip submitted. Awaiting admin audit.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsBoostModalOpen(true)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#0B1530',
                        color: '#FFC700',
                        border: '1px solid rgba(255, 199, 0, 0.4)',
                        borderRadius: '12px',
                        fontWeight: '800',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 12px rgba(11, 21, 48, 0.15)',
                        transition: 'all 0.2s'
                      }}
                    >
                      <Clock size={15} color="#FFC700" />
                      View Pending Audit Status
                    </button>
                  </div>
                ) : (!activeVehicle.approvalStatus || activeVehicle.approvalStatus === 'approved') ? (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.25) 100%)',
                    border: '1.5px solid rgba(245, 158, 11, 0.5)',
                    borderRadius: '16px',
                    padding: '16px',
                    margin: '16px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: '#0B1530',
                        color: '#FFC700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '900',
                        fontSize: '16px',
                        flexShrink: 0
                      }}>
                        💥
                      </div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '900', color: '#0F172A' }}>
                          Promote & Boost Ad
                        </h4>
                        <p style={{ margin: '2px 0 0', fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                          Page 1 bump, Top Pinning, or Urgent Tag.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsBoostModalOpen(true)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#0B1530',
                        color: '#FFC700',
                        border: '1px solid rgba(255, 199, 0, 0.4)',
                        borderRadius: '12px',
                        fontWeight: '800',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 12px rgba(11, 21, 48, 0.15)',
                        transition: 'all 0.2s'
                      }}
                    >
                      <Sparkles size={15} color="#FFC700" />
                      Boost Listing Now (From Rs. 300)
                    </button>
                  </div>
                ) : null
              )}

              <div className="vd-hero-facts" aria-label="Quick vehicle facts">
                {heroFacts.map(fact => (
                  <div className="vd-hero-fact" key={fact.label}>
                    <span>{fact.label}</span>
                    <strong>{fact.value}</strong>
                  </div>
                ))}
              </div>

              <div className="vd-actions">
                {phoneDigits ? (
                  <a className="vd-action vd-action--primary" href={`tel:${phoneDigits}`}>
                    <Phone size={19} aria-hidden="true" />
                    Call Seller
                  </a>
                ) : (
                  <button className="vd-action vd-action--disabled" type="button" disabled>
                    <Phone size={19} aria-hidden="true" />
                    Call unavailable
                  </button>
                )}

                {whatsappNumber && (
                  <a
                    className="vd-action vd-action--whatsapp"
                    href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp aria-hidden="true" />
                    WhatsApp
                  </a>
                )}

                {sellerEmail ? (
                  <a
                    className="vd-action vd-action--secondary"
                    href={`mailto:${sellerEmail}?subject=${emailSubject}&body=${emailBody}`}
                  >
                    <Mail size={18} aria-hidden="true" />
                    Email Seller
                  </a>
                ) : (
                  <button className="vd-action vd-action--secondary" type="button">
                    <MessageCircle size={18} aria-hidden="true" />
                    Chat with Owner
                  </button>
                )}
              </div>
            </section>

            <section 
              className={`vd-seller-card relative overflow-hidden ${isShowroom ? 'vd-seller-card--dark shadow-lg' : ''}`}
              style={{
                backgroundImage: isShowroom && sellerCover ? `url(${sellerCover})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {isShowroom ? (
                <>
                  {/* Glassmorphic blur overlay */}
                  <div className="absolute inset-0 bg-slate-950/45 backdrop-blur-[2px] z-0" />
                  
                  <div className="vd-seller-content relative z-10">
                    <div className="vd-seller-profile">
                      <div className="vd-seller-avatar border-2 border-white/20 shadow-md">
                        {sellerAvatar ? (
                          <img src={sellerAvatar} alt={`${sellerName} logo`} />
                        ) : (
                          <span>{getInitials(sellerName)}</span>
                        )}
                      </div>
                      <div className="vd-seller-main">
                        <div className="vd-seller-name text-white">
                          <strong className="text-white">{sellerName}</strong>
                          <VerifiedBadge />
                        </div>
                        <span className="text-emerald-400 font-bold">{sellerTypeLabel}</span>
                      </div>
                    </div>

                    <div className="vd-contact-list vd-contact-list--dark">
                      {sellerPhone && <ContactLine icon={Phone} label="Hotline" value={sellerPhone} href={`tel:${phoneDigits}`} />}
                      {sellerEmail && <ContactLine icon={Mail} label="Email" value={sellerEmail} href={`mailto:${sellerEmail}`} />}
                    </div>

                    <div className="vd-safety-tip vd-safety-tip--dark">
                      <ShieldCheck size={20} className="text-amber-400 shrink-0" aria-hidden="true" />
                      <div>
                        <strong>Safety Tip</strong>
                        <p>Meet in a public place and inspect the vehicle before making any payment.</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="vd-seller-content">
                  <div className="vd-seller-profile">
                    <div className="vd-seller-avatar">
                      {sellerAvatar ? (
                        <img src={sellerAvatar} alt={`${sellerName} logo`} />
                      ) : (
                        <span>{getInitials(sellerName)}</span>
                      )}
                    </div>
                    <div className="vd-seller-main">
                      <div className="vd-seller-name">
                        <strong>{sellerName}</strong>
                      </div>
                      <span>{sellerTypeLabel}</span>
                    </div>
                  </div>

                  <div className="vd-contact-list">
                    {sellerPhone && <ContactLine icon={Phone} label="Hotline" value={sellerPhone} href={`tel:${phoneDigits}`} />}
                    {sellerEmail && <ContactLine icon={Mail} label="Email" value={sellerEmail} href={`mailto:${sellerEmail}`} />}
                  </div>

                  <div className="vd-safety-tip">
                    <ShieldCheck size={20} aria-hidden="true" />
                    <div>
                      <strong>Safety Tip</strong>
                      <p>Meet in a public place and inspect the vehicle before making any payment.</p>
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="vd-share-card">
              <div className="vd-share-heading">
                <Share2 size={19} aria-hidden="true" />
                <h2>Share this Ad</h2>
              </div>
              <div className="vd-share-actions">
                <button type="button" className="vd-share-button" onClick={handleShare}>
                  <Copy size={17} aria-hidden="true" />
                  Copy Link
                </button>
                <button
                  type="button"
                  className={`vd-share-button ${saved ? 'vd-share-button--saved' : ''}`}
                  onClick={() => setSaved(current => !current)}
                >
                  <Heart size={17} aria-hidden="true" fill={saved ? 'currentColor' : 'none'} />
                  {saved ? 'Saved' : 'Save Ad'}
                </button>
              </div>
              {shareMessage && <p className="vd-share-status">{shareMessage}</p>}
            </section>
          </aside>
        </div>
      </div>

      {/* Boost Post Modal Component */}
      <BoostPostModal
        isOpen={isBoostModalOpen}
        onClose={() => setIsBoostModalOpen(false)}
        vehicle={activeVehicle}
      />

      {(phoneDigits || whatsappNumber) && (
        <div className="vd-mobile-contact-bar" aria-label="Quick contact actions">
          {phoneDigits && (
            <a className="vd-mobile-action vd-mobile-action--call" href={`tel:${phoneDigits}`}>
              <Phone size={18} aria-hidden="true" />
              Call
            </a>
          )}
          {whatsappNumber && (
            <a
              className="vd-mobile-action vd-mobile-action--whatsapp"
              href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaWhatsapp aria-hidden="true" />
              WhatsApp
            </a>
          )}
        </div>
      )}
    </main>
  );
};

const Gallery = ({ activePhoto, idx, listingTitle, next, photos, prev, setIdx }) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') setIsLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, next, prev]);

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLightboxOpen]);

  return (
    <section className="vd-gallery-card" aria-label="Vehicle gallery">
      <div className="vd-gallery-stage">
        {activePhoto ? (
          <>
            <img className="vd-gallery-backdrop" src={activePhoto} alt="" aria-hidden="true" />
            <div
              className="vd-gallery-image-wrap"
              onClick={() => setIsLightboxOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsLightboxOpen(true);
                }
              }}
              title="Click to view full screen"
            >
              <img
                className="vd-gallery-image"
                src={activePhoto}
                alt={`${listingTitle} view ${idx + 1}`}
                decoding="async"
              />
            </div>
            <button
              type="button"
              className="vd-gallery-expand-btn"
              onClick={() => setIsLightboxOpen(true)}
              aria-label="View full screen"
              title="View full screen"
            >
              <Maximize2 size={16} aria-hidden="true" />
              <span>Full View</span>
            </button>
          </>
        ) : (
          <div className="vd-gallery-empty">
            <Camera size={34} aria-hidden="true" />
            <span>No images available</span>
          </div>
        )}

        {photos.length > 1 && (
          <>
            <button
              type="button"
              className="vd-gallery-nav vd-gallery-nav--prev"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous image"
              title="Previous image"
            >
              <ChevronLeft size={24} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="vd-gallery-nav vd-gallery-nav--next"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next image"
              title="Next image"
            >
              <ChevronRight size={24} aria-hidden="true" />
            </button>
          </>
        )}

        <div className="vd-photo-count">
          <Camera size={15} aria-hidden="true" />
          {photos.length ? `${idx + 1} / ${photos.length}` : '0 photos'}
        </div>
      </div>

      {photos.length > 1 && (
        <div className="vd-thumb-strip" aria-label="Gallery thumbnails">
          {photos.map((photo, index) => (
            <button
              type="button"
              key={`${photo}-${index}`}
              className={`vd-thumb ${idx === index ? 'is-active' : ''}`}
              onClick={() => setIdx(index)}
              aria-label={`Show photo ${index + 1}`}
              aria-current={idx === index ? 'true' : undefined}
            >
              <img src={photo} alt="" aria-hidden="true" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox / Fullscreen Modal */}
      {isLightboxOpen && activePhoto && (
        <div
          className="vd-lightbox-overlay"
          onClick={() => setIsLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Full size photo viewer"
        >
          <div className="vd-lightbox-header" onClick={(e) => e.stopPropagation()}>
            <span className="vd-lightbox-counter">
              <Camera size={16} aria-hidden="true" />
              {photos.length ? `${idx + 1} of ${photos.length}` : ''}
            </span>
            <button
              type="button"
              className="vd-lightbox-close"
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Close full view"
            >
              <X size={24} aria-hidden="true" />
            </button>
          </div>

          <div className="vd-lightbox-stage" onClick={(e) => e.stopPropagation()}>
            <img
              className="vd-lightbox-img"
              src={activePhoto}
              alt={`${listingTitle} full view ${idx + 1}`}
            />

            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  className="vd-lightbox-nav vd-lightbox-nav--prev"
                  onClick={prev}
                  aria-label="Previous image"
                >
                  <ChevronLeft size={32} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="vd-lightbox-nav vd-lightbox-nav--next"
                  onClick={next}
                  aria-label="Next image"
                >
                  <ChevronRight size={32} aria-hidden="true" />
                </button>
              </>
            )}
          </div>

          {photos.length > 1 && (
            <div className="vd-lightbox-footer" onClick={(e) => e.stopPropagation()}>
              <div className="vd-lightbox-thumbs">
                {photos.map((photo, index) => (
                  <button
                    type="button"
                    key={`lb-${photo}-${index}`}
                    className={`vd-lightbox-thumb ${idx === index ? 'is-active' : ''}`}
                    onClick={() => setIdx(index)}
                    aria-label={`Jump to photo ${index + 1}`}
                  >
                    <img src={photo} alt="" aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const SpecItem = ({ icon: Icon, label, value }) => (
  <div className="vd-spec-item">
    <span className="vd-spec-item__icon">
      {Icon && <Icon size={18} aria-hidden="true" />}
    </span>
    <div className="vd-spec-item__content">
      <span className="vd-spec-item__label">{label}</span>
      <strong className="vd-spec-item__value">{hasValue(value) ? value : 'Not listed'}</strong>
    </div>
  </div>
);

const MetaLine = ({ icon: Icon, value, verified = false }) => (
  <div className="vd-meta-line">
    <Icon size={17} aria-hidden="true" />
    <span>{value}</span>
    {verified && <VerifiedBadge />}
  </div>
);

const VerifiedBadge = () => (
  <img 
    src={verifiedIcon} 
    alt="Verified showroom partner" 
    title="Verified showroom partner" 
    style={{ width: '16px', height: '16px', objectFit: 'contain', flexShrink: 0 }}
  />
);

const ContactLine = ({ icon: Icon, label, value, href }) => {
  const content = (
    <>
      <span className="vd-contact-icon">
        <Icon size={17} aria-hidden="true" />
      </span>
      <span>{label}</span>
      <strong>{value}</strong>
    </>
  );

  if (href) {
    return (
      <a className="vd-contact-line" href={href}>
        {content}
      </a>
    );
  }

  return <div className="vd-contact-line">{content}</div>;
};

export default VehicleDetails;
